import Phaser from "phaser";
import type { CutsceneSlide } from "@/core";
import type { GameApp } from "../app/GameApp";
import { REGISTRY_KEYS } from "../app/GameApp";
import { mountDom } from "@/ui/domHelpers";

export class CutsceneScene extends Phaser.Scene {
  private app!: GameApp;
  private slides: CutsceneSlide[] = [];
  private currentIdx = 0;

  private dom?: Phaser.GameObjects.DOMElement;
  private speakerEl?: HTMLElement;
  private textEl?: HTMLElement;

  private typeTimer?: Phaser.Time.TimerEvent;
  private isTyping = false;
  private currentFullText = "";
  private closeTimeoutId?: number;

  constructor() {
    super({ key: "CutsceneScene" });
  }

  create() {
    this.app = this.game.registry.get("app") as GameApp;
    this.mount();

    const startSlides = (slides: CutsceneSlide[] | null) => {
      if (!slides?.length) return;
      window.clearTimeout(this.closeTimeoutId);
      this.closeTimeoutId = undefined;
      this.slides = slides;
      this.currentIdx = 0;
      this.isTyping = false;
      this.time.delayedCall(150, () => this.showSlide());
    };

    startSlides(this.registry.get(REGISTRY_KEYS.currentCutscene) as CutsceneSlide[] | null);

    const onChange = (_parent: unknown, value: CutsceneSlide[] | null) => startSlides(value);
    this.registry.events.on(`changedata-${REGISTRY_KEYS.currentCutscene}`, onChange);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.registry.events.off(`changedata-${REGISTRY_KEYS.currentCutscene}`, onChange);
    });
  }

  private mount() {
    const html = `
      <div class="cutscene">
        <button class="cutscene__skip" data-cutscene-skip>Пропустить</button>
        <div class="cutscene__panel">
          <span class="cutscene__speaker"></span>
          <p class="cutscene__text"></p>
        </div>
      </div>
    `;
    this.dom = mountDom(this, html);
    const root = this.dom.node as HTMLElement;
    this.speakerEl = root.querySelector(".cutscene__speaker") as HTMLElement;
    this.textEl = root.querySelector(".cutscene__text") as HTMLElement;

    root.addEventListener("click", (e) => {
      if ((e.target as HTMLElement).closest("[data-cutscene-skip]")) {
        this.close();
        return;
      }
      if (this.isTyping) this.finishTyping();
      else this.nextSlide();
    });

    root.addEventListener("contextmenu", (e) => {
      e.preventDefault();
      this.finishTyping();
      this.nextSlide();
    });
  }

  private showSlide() {
    if (this.currentIdx >= this.slides.length) {
      this.close();
      return;
    }
    const slide = this.slides[this.currentIdx];
    if (this.speakerEl) this.speakerEl.textContent = slide.speaker ?? "";
    this.currentFullText = slide.text;
    this.typewriter(slide.text, 30);
  }

  private nextSlide() {
    this.currentIdx++;
    this.showSlide();
  }

  private typewriter(fullText: string, speed = 30) {
    if (!this.textEl) return;
    this.textEl.textContent = "";
    let idx = 0;
    this.isTyping = true;

    this.typeTimer?.remove();
    this.typeTimer = this.time.addEvent({
      delay: speed,
      callback: () => {
        idx++;
        if (this.textEl) this.textEl.textContent = fullText.substring(0, idx);
        if (idx >= fullText.length) {
          this.typeTimer?.remove();
          this.typeTimer = undefined;
          this.isTyping = false;
        }
      },
      loop: true,
    });
  }

  private finishTyping() {
    if (!this.isTyping) return;
    this.typeTimer?.remove();
    this.typeTimer = undefined;
    if (this.textEl) this.textEl.textContent = this.currentFullText;
    this.isTyping = false;
  }

  private close() {
    this.finishTyping();
    const appRef = this.app;
    this.closeTimeoutId = window.setTimeout(() => appRef.closeCutscene(), 250);
  }

  shutdown() {
    this.typeTimer?.remove();
    window.clearTimeout(this.closeTimeoutId);
    this.dom = undefined;
    this.speakerEl = undefined;
    this.textEl = undefined;
  }
}
